<template>
  <div class="px-4 pb-4 pt-1">
    <p class="text-textPrimary mb-4 text-left">
      Selecione um modelo para importar as configurações
    </p>

    <SearchInput
      v-model="searchQuery"
      label="Buscar unidades ou escalas"
      placeholder="Digite o nome da unidade ou escala"
      persistent-placeholder
      :search-function="handleSearch"
    />

    <div class="max-h-[320px] overflow-y-auto flex flex-col gap-2 relative">
      <div
        v-if="isDeleting"
        class="absolute inset-0 bg-white/60 z-10 flex items-center justify-center"
      >
        <Spinner color="primary" size="3rem" />
      </div>

      <div
        v-for="template in filteredTemplates"
        :key="template.id"
        class="flex items-center justify-between rounded-lg px-4 py-3 cursor-pointer transition-colors"
        :class="
          modelValue === template.id
            ? 'bg-[#E8F4F7] border border-primary'
            : 'bg-[#F0F0F4] hover:bg-gray-200'
        "
        @click="$emit('update:modelValue', template.id)"
      >
        <RadioButton
          :id="`template-${template.id}`"
          name="config-template"
          :value="template.id"
          :model-value="modelValue ?? ''"
          @update:model-value="$emit('update:modelValue', Number($event))"
        >
          <div class="text-left">
            <span class="text-xs font-bold text-textPrimary">Nome</span>
            <p class="text-textPrimary">{{ template.name }}</p>
          </div>
        </RadioButton>

        <button
          class="p-1 rounded hover:bg-red-50 transition-colors"
          @click.stop="handleDelete(template.id)"
        >
          <DeleteIcon color="#C62828" width="24" height="24" />
        </button>
      </div>

      <p v-if="filteredTemplates.length === 0" class="text-sm text-gray-500 text-center py-6">
        Nenhum modelo encontrado
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import RadioButton from '@/components/designSystem/RadioButton/index.vue'
import DeleteIcon from '@/assets/icons/delete.vue'
import Spinner from '@/components/others/Spinner.vue'
import type { ApprovalFlowTemplateDetail } from '@/Network/Types/Responses/ApprovalFlowTemplateResponses'

interface ConfigTemplate {
  id: number
  name: string
}

const props = defineProps<{
  modelValue: number | null
  savedTemplate?: ConfigTemplate | null
  templates?: ApprovalFlowTemplateDetail[]
  isDeleting?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | null): void
  (e: 'delete', id: number): void
}>()

const searchQuery = ref('')

const templateList = computed<ConfigTemplate[]>(() => {
  const base: ConfigTemplate[] = (props.templates ?? []).map((t) => ({
    id: t.id,
    name: t.name
  }))
  if (props.savedTemplate && !base.find((t) => t.id === props.savedTemplate!.id)) {
    return [props.savedTemplate, ...base]
  }
  return base
})

const filteredTemplates = computed(() => {
  if (!searchQuery.value) return templateList.value
  const q = searchQuery.value.toLowerCase()
  return templateList.value.filter((t) => t.name.toLowerCase().includes(q))
})

const handleSearch = async () => {
  // filtro local via computed — sem necessidade de chamada extra
}

const handleDelete = (id: number) => {
  emit('delete', id)
}

const removeTemplate = () => {
  emit('update:modelValue', null)
}

defineExpose({ removeTemplate })
</script>
